import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { chatService, NewSessionContent } from '../services';
import { isAuthorizationError } from '../types/errors';

interface UseNewSessionContentReturn {
  content: NewSessionContent | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useNewSessionContent = (): UseNewSessionContentReturn => {
  const [content, setContent] = useState<NewSessionContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authError, setAuthError] = useState<Error | null>(null);
  const { isAuthenticated } = useAuth();

  // Throw authorization errors during render so they're caught by error boundary
  if (authError) {
    throw authError;
  }

  const fetchContent = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const newContent = await chatService.getNewSessionMessage();
      setContent(newContent);
    } catch (err) {
      // Store authorization errors to be thrown during render
      if (isAuthorizationError(err)) {
        // Don't log auth errors - they're expected and handled by the error boundary
        setIsLoading(false);
        setAuthError(err as Error);
        return;
      }
      
      const errorMessage = err instanceof Error ? err.message : 'Failed to load new session content';
      console.error('useNewSessionContent: Fetch failed', err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Don't fetch if we have an authorization error
    if (authError) {
      return;
    }
    
    // Only fetch if authenticated and we don't have content yet or had an auth error
    if (isAuthenticated && (!content || (error && error.includes('access token')))) {
      fetchContent();
    }
  }, [isAuthenticated, content, error, fetchContent, authError]);

  return {
    content,
    isLoading,
    error,
    refetch: fetchContent
  };
};
